import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSubmissionsByAssignmentId, getSubmissionById } from '../../../service';
import DropdownMenu from '../../DropdownMenu';
import { useAssignmentSubmissionsStatus } from '../../../hooks/useAssignmentFeedbackRun';
import RunStatusNotice from './RunStatusNotice';
import {
  FEEDBACK_STATUS,
  feedbackStatusOf,
  findSubmissionRow,
  isFeedbackInFlight,
} from '../utils/feedbackRun';
import { htmlToPlainText } from '../utils/html';
import {
  DraftsMainContent,
  DraftsMainHeader,
  DraftsMainTitle,
  DraftsMainSubtitle,
  SubmissionNavigation,
  SubmissionNavigationButton,
  SubmissionInfo,
  ResponseSection,
  ResponseText,
  StudentDropdownWrapper,
} from '../styles';

// A student still generating stays selectable: their response text was extracted
// before feedback was ever requested, so there is already something to read here.
// A failed one is marked, not hidden.
const FEEDBACK_SUFFIX = {
  [FEEDBACK_STATUS.PENDING]: ' · waiting for text',
  [FEEDBACK_STATUS.GENERATING]: ' · generating…',
  [FEEDBACK_STATUS.FAILED]: " · couldn't be marked",
};

/**
 * Calculates word count from HTML or plain text.
 * Falls back to client-side counting when server wordCount is missing or 0.
 */
const calculateWordCount = (text) => {
  if (!text) return 0;
  const plainText = htmlToPlainText(text).trim();
  if (!plainText) return 0;
  // Split on whitespace and filter out empty strings
  return plainText.split(/\s+/).filter(Boolean).length;
};

/**
 * DraftSubmissionsView - Displays submissions for a selected draft assignment
 *
 * Features:
 * - Fetches all submissions for the assignment
 * - Shows student dropdown to select submissions
 * - Displays selected submission details
 * - Prev/Next navigation between submissions
 */
export const DraftSubmissionsView = ({ assignmentId, assignmentTitle }) => {
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Fetch all submissions for this assignment
  const { data: submissionsData, isLoading: isLoadingList } = useQuery({
    queryKey: ['submissions', assignmentId],
    queryFn: () => getSubmissionsByAssignmentId(assignmentId),
    enabled: !!assignmentId,
  });

  const submissions = submissionsData || [];

  // Auto-select first submission when data loads or assignmentId changes
  useEffect(() => {
    if (submissions.length > 0) {
      const firstSubmissionId = submissions[0].id || submissions[0]._id;
      setSelectedSubmissionId(firstSubmissionId);
      setSelectedIndex(0);
    } else {
      setSelectedSubmissionId(null);
      setSelectedIndex(0);
    }
  }, [submissions.length, assignmentId]);

  // Live per-student feedback state, shared with everything else watching this
  // assignment: one query key, one timer. It stops on its own once the run
  // settles, so an assignment nobody asked feedback for is read once.
  const { data: runStatus, error: runStatusError, refetch: retryStatus, isFetching: isFetchingStatus } = useAssignmentSubmissionsStatus(assignmentId);
  const selectedRow = runStatusError ? null : findSubmissionRow(runStatus, selectedSubmissionId);

  // Fetch details of the selected submission, re-reading only while its own row
  // says something is still coming. The old "poll until jeddaiFeedbackReceivedAt"
  // rule never stopped for a submission nobody had requested feedback for.
  const { data: currentSubmission, isLoading: isLoadingSubmission } = useQuery({
    queryKey: ['submission', selectedSubmissionId],
    queryFn: () => getSubmissionById(selectedSubmissionId),
    enabled: !!selectedSubmissionId,
    refetchInterval: () => (isFeedbackInFlight(selectedRow) ? 10000 : false),
  });

  // Build dropdown menu items from submissions
  const dropdownItems = submissions.map((submission, index) => {
    const submissionId = submission.id || submission._id;
    const studentName = submission.studentName ||
                        submission.student?.name ||
                        submission.student?.studentName ||
                        `Student ${index + 1}`;
    const feedbackStatus = feedbackStatusOf(
      runStatusError ? null : findSubmissionRow(runStatus, submissionId),
    );

    return {
      id: submissionId,
      title: `${studentName}${FEEDBACK_SUFFIX[feedbackStatus] || ''}`,
      studentName,
      feedbackStatus,
      index,
    };
  });

  const handleStudentSelect = (item) => {
    setSelectedSubmissionId(item.id);
    setSelectedIndex(item.index);
  };

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      const prevSubmission = submissions[newIndex];
      setSelectedSubmissionId(prevSubmission.id || prevSubmission._id);
      setSelectedIndex(newIndex);
    }
  };

  const handleNext = () => {
    if (selectedIndex < submissions.length - 1) {
      const newIndex = selectedIndex + 1;
      const nextSubmission = submissions[newIndex];
      setSelectedSubmissionId(nextSubmission.id || nextSubmission._id);
      setSelectedIndex(newIndex);
    }
  };

  if (isLoadingList) {
    return (
      <DraftsMainContent>
        <DraftsMainTitle>Loading submissions...</DraftsMainTitle>
      </DraftsMainContent>
    );
  }

  if (!submissions || submissions.length === 0) {
    return (
      <DraftsMainContent>
        <DraftsMainTitle>{assignmentTitle || 'Draft'}</DraftsMainTitle>
        <DraftsMainSubtitle>
          No submissions yet. Create submissions via bulk upload.
        </DraftsMainSubtitle>
      </DraftsMainContent>
    );
  }

  const selectedDropdownItem = dropdownItems[selectedIndex];
  const currentStudentName = selectedDropdownItem?.studentName || 'Student';

  return (
    <DraftsMainContent>
      <RunStatusNotice error={runStatusError} retry={retryStatus} fetching={isFetchingStatus} />
      <DraftsMainHeader>
        <DraftsMainTitle>{assignmentTitle || 'Draft'}</DraftsMainTitle>
        <DraftsMainSubtitle>
          {submissions.length} submission{submissions.length !== 1 ? 's' : ''} from bulk upload
        </DraftsMainSubtitle>
      </DraftsMainHeader>

      <StudentDropdownWrapper>
        <DropdownMenu
          primaryText={selectedDropdownItem?.title || 'Select student'}
          menuItems={dropdownItems}
          selectedIndex={selectedIndex}
          onItemSelected={handleStudentSelect}
          fullWidth
          borderRadius={8}
        />
      </StudentDropdownWrapper>

      {isLoadingSubmission ? (
        <DraftsMainSubtitle>Loading submission details...</DraftsMainSubtitle>
      ) : currentSubmission ? (
        <>
          <ResponseSection>
            <h3>Question</h3>
            <ResponseText>
              {currentSubmission.assignment?.questions?.[0]?.question ||
               currentSubmission.question?.text ||
               currentSubmission.assignment?.question?.text ||
               'No question text available'}
            </ResponseText>
          </ResponseSection>

          <ResponseSection>
            <h3>Response by {currentStudentName}</h3>
            <ResponseText>
              {currentSubmission.answers?.[0]?.answer?.responseText ||
               currentSubmission.answer?.responseText ||
               currentSubmission.answers?.[0]?.responseText ||
               'No response text available'}
            </ResponseText>
          </ResponseSection>

          <SubmissionInfo>
            {(() => {
              const serverWordCount = currentSubmission.answers?.[0]?.answer?.wordCount || currentSubmission.answer?.wordCount;
              const responseText = currentSubmission.answers?.[0]?.answer?.responseText ||
                                   currentSubmission.answer?.responseText ||
                                   currentSubmission.answers?.[0]?.responseText;
              const wordCount = serverWordCount || calculateWordCount(responseText);
              return wordCount > 0 && <span>{wordCount} words</span>;
            })()}
            {(currentSubmission.answers?.[0]?.answer?.fileUrls?.length > 0 || currentSubmission.answer?.fileUrls?.length > 0) && (
              <span>{currentSubmission.answers?.[0]?.answer?.fileUrls?.length || currentSubmission.answer.fileUrls.length} file(s)</span>
            )}
          </SubmissionInfo>
        </>
      ) : (
        <DraftsMainSubtitle>Unable to load submission details</DraftsMainSubtitle>
      )}

      <SubmissionNavigation>
        <SubmissionNavigationButton
          type="button"
          onClick={handlePrevious}
          disabled={selectedIndex === 0}
        >
          ◀ Previous
        </SubmissionNavigationButton>
        <span>
          {selectedIndex + 1} of {submissions.length}
        </span>
        <SubmissionNavigationButton
          type="button"
          onClick={handleNext}
          disabled={selectedIndex === submissions.length - 1}
        >
          Next ▶
        </SubmissionNavigationButton>
      </SubmissionNavigation>
    </DraftsMainContent>
  );
};
